import React, { FC, useCallback, useContext, useEffect, useState } from 'react';

import { iconSize, titleSize } from '../utils';
import { UnitEvent } from '../../../core/Unit/UnitEvent';
import { BreakpointsContext } from '@src/providers/Breakpoints';
import { Box } from '@src/atoms/Box';
import { Text } from '@src/atoms/Text';
import { Icon } from '@src/atoms/Icon';
import { Collapse } from '@src/atoms/Collapse';
import { TSize } from '@src/models/common';
import { classnames } from '@src/utils/classnames';
import { getClass } from '@src/utils/getClass';
import { IAccordionItem } from '../models';
import { IDisableAnimationProps } from '@src/models/props';

import styled from './AccordionItem.scss';

export interface IAccordionItemProps extends IAccordionItem, IDisableAnimationProps {
  size: TSize;
  nestedInteractive?: boolean;
}

export const AccordionItem: FC<IAccordionItemProps> = ({
  open: propsOpen,
  content,
  title,
  size,
  count,
  disableAnimation,
  id,
  nestedInteractive,
}) => {
  const [open, setOpen] = useState(propsOpen);

  const handleClick = useCallback(
    (e) => {
      /** Для ссылок и кнопок (тегов <a> и <button>) будет содержать значение. Если клик прошел по ним, то элемент аккордиона не нужно закрывать */
      const isNestedClick = !!nestedInteractive && e.target.closest(`[data-id="accordionItem_nested_${id}"]`) !== null;

      setOpen((prev) => {
        /** Если вложенный клик по интерактивному элементу не меняем значение */
        const newValue = isNestedClick ? prev : !prev;

        /** Создаем объект события для обработки юнитом */
        e.unit = new UnitEvent({
          details: {
            kind: 'click',
            dataId: '$self',
            kindData: {
              open: String(newValue),
            },
          },
        });

        return newValue;
      });
    },
    [id, nestedInteractive],
  );

  const { isMobile } = useContext(BreakpointsContext);

  const classes = classnames(styled.root, getClass(styled, 'size', size), {
    [styled.mobile]: isMobile,
  });

  const contentClasses = classnames(styled.content, {
    [styled.contentOpen]: open,
  });

  const nestedClass = classnames(nestedInteractive && styled.nestedInteractive);

  useEffect(() => {
    setOpen(propsOpen);
  }, [propsOpen]);

  return (
    <div className={classes} onClick={handleClick} data-open={open} data-id={`accordionItem_${id}`}>
      <div className={styled.title}>
        <Box alignItems={'start'} justifyContent={'spaceBetween'} fullWidth>
          <Text id={'titleText'} className={styled.titleText} weight="bold" size={titleSize(size, isMobile)}>
            {title}
            {!!count && <span className={styled.counter}>&bull; {count}</span>}
          </Text>
          <Icon name={open ? 'minus' : 'plus'} size={iconSize(size, isMobile)} />
        </Box>
      </div>
      <Collapse
        open={open}
        className={contentClasses}
        disableAnimation={disableAnimation}
        data-id={`accordionItem_content_${id}`}
      >
        <div data-id={`accordionItem_nested_${id}`} className={nestedClass}>
          {content}
        </div>
      </Collapse>
    </div>
  );
};
